import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ClipboardSignature, Info, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { doa, type DoaVersionRow } from '@/api/client';
import { DoaVersionList } from './DoaVersionList';
import { DoaVersionDetail } from './DoaVersionDetail';
import { DoaBinderScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-10 — Delegation of Authority workspace page.
 *
 * URL: /binder/:binderId/doa
 *
 * Layout:
 *   - Header (back-link, title, optional "+ New DoA version" CTA)
 *   - Left rail: DoaVersionList (one card per version, click → select)
 *   - Center: DoaVersionDetail for the selected version (rows + lifecycle)
 *
 * Default selection: the currently-active version (from getActive). If there
 * is no active version yet, the first row of listVersions (newest first) is
 * selected — falling back to "no selection" only when the binder has zero
 * versions on file.
 *
 * The "+ New DoA version" button is only shown when no draft exists, so we
 * never accidentally create two parallel drafts on the same binder.
 */
export default function DoaPage() {
  const { binderId = '' } = useParams<{ binderId: string }>();
  const qc = useQueryClient();

  // ── Queries ─────────────────────────────────────────────────────────
  const activeQ = useQuery({
    queryKey: ['binder', binderId, 'doa', 'active'],
    queryFn: () => doa.getActive(binderId),
    enabled: !!binderId,
  });

  // Phase 2 Defect 2.6 — poll the version list while any version is
  // in_review so the sidebar status badges update automatically as
  // rows get signed / the version activates.
  const versionsQ = useQuery({
    queryKey: ['binder', binderId, 'doa', 'versions'],
    queryFn: () => doa.listVersions(binderId),
    enabled: !!binderId,
    refetchInterval: (query) => {
      const versions = query.state.data?.versions ?? [];
      return versions.some((v) => v.status === 'in_review') ? 10_000 : false;
    },
    staleTime: 8_000,
  });

  const versions: DoaVersionRow[] = versionsQ.data?.versions ?? [];
  const activeVersion = activeQ.data?.version ?? null;

  // ── Selection state ─────────────────────────────────────────────────
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Seed selection once: active version → newest version → null. Never overwrite
  // a user pick once made.
  useEffect(() => {
    if (selectedVersionId) return;
    if (activeVersion) {
      setSelectedVersionId(activeVersion.doa_version_id);
      return;
    }
    const newest = versions[0];
    if (newest) setSelectedVersionId(newest.doa_version_id);
  }, [activeVersion, versions, selectedVersionId]);

  // ── Derived UI state ────────────────────────────────────────────────
  const hasDraft = useMemo(
    () => versions.some((v) => v.status === 'draft'),
    [versions],
  );

  // ── Create-draft mutation ───────────────────────────────────────────
  const createDraft = useMutation({
    mutationFn: () => doa.createDraft(binderId, {}),
    onSuccess: (res) => {
      toast.success(`Created draft DoA v${res.version_number}`);
      qc.invalidateQueries({ queryKey: ['binder', binderId, 'doa', 'versions'] });
      qc.invalidateQueries({ queryKey: ['binder', binderId, 'doa', 'active'] });
      setSelectedVersionId(res.doa_version_id);
    },
    onError: (err) => {
      toast.error(
        errorText(err, 'Failed to create draft DoA version'),
      );
    },
  });

  // ── Loading / error gates ───────────────────────────────────────────
  if (activeQ.isLoading || versionsQ.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading DoA…
      </div>
    );
  }

  if (activeQ.isError || versionsQ.isError) {
    const err = activeQ.error ?? versionsQ.error;
    return (
      <div className="space-y-3 p-6">
        <Link
          to={`/binder/${encodeURIComponent(binderId)}`}
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Back to binder
        </Link>
        <div className={`flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(err)}`}>
          {isPermissionError(err) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>
            {errorText(err, 'Failed to load DoA.')}
          </span>
        </div>
      </div>
    );
  }

  const gridHref = `/binder/${encodeURIComponent(binderId)}/doa/grid`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Dark top bar — matches the grid editor's header for visual continuity */}
      <header className="bg-ink-primary text-white px-5 py-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardSignature className="h-5 w-5 text-accent-300 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-base font-semibold leading-tight">
              Delegation of Authority
              <DoaBinderScopeBadge binderRef={binderId} />
            </div>
            <div className="text-[11px] text-white/60 leading-tight">
              {versions.length} version{versions.length === 1 ? '' : 's'} ·{' '}
              {activeVersion
                ? `Active ${activeVersion.reference_number ?? `v${activeVersion.version_number}`}`
                : 'No active version'}
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to={`/binder/${encodeURIComponent(binderId)}`}
            className="inline-flex items-center gap-1.5 rounded bg-white/10 px-3 h-9 text-sm font-medium text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Binder
          </Link>
          {!hasDraft ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded bg-accent-500 px-3.5 h-9 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-60"
              disabled={createDraft.isPending}
              onClick={() => createDraft.mutate()}
            >
              {createDraft.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              New DoA version
            </button>
          ) : null}
        </div>
      </header>

      {/* Tab strip — mirrors the grid editor's tab bar */}
      <nav className="bg-white border-b border-surface-border px-5 flex items-center gap-6">
        <div className="text-sm text-ink-primary font-semibold py-3 border-b-2 border-accent-500">
          Overview
        </div>
        <Link
          to={gridHref}
          className="text-sm text-ink-muted hover:text-ink-primary py-3 border-b-2 border-transparent"
        >
          Delegations
        </Link>
      </nav>

      {/* Two-column workspace */}
      <div className="flex-1 min-h-0 p-4">
        <div className="flex h-full min-h-0 gap-4">
        {/* Left rail — version list */}
        <aside className="w-1/4 min-w-0 shrink-0">
          <div className="card flex h-full min-h-0 flex-col overflow-hidden">
            <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
              Versions
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              <DoaVersionList
                binderId={binderId}
                versions={versions}
                selectedVersionId={selectedVersionId}
                onSelect={(id) => setSelectedVersionId(id)}
              />
            </div>
          </div>
        </aside>

        {/* Center — version detail */}
        <section className="min-w-0 flex-1">
          <div className="h-full min-h-0 overflow-hidden">
            {selectedVersionId ? (
              <DoaVersionDetail
                binderId={binderId}
                versionId={selectedVersionId}
              />
            ) : (
              <div className="card flex h-full items-center justify-center p-8 text-sm text-ink-muted">
                Select a version from the left rail, or create a new draft.
              </div>
            )}
          </div>
        </section>
        </div>
      </div>
    </div>
  );
}
